import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

interface AutoCompleteProps {
  children: (ref: React.RefObject<HTMLDivElement[]>) => React.ReactNode;
  onSelect?: (value: string) => void;
  autocomplete?: {
    value: string;
    label: string;
  }[];
  inputRef: React.RefObject<HTMLInputElement>;
}

const AutoComplete = ({
  children,
  onSelect,
  autocomplete,
  inputRef,
}: AutoCompleteProps) => {
  const autoCompleteRef = useRef<HTMLDivElement[]>([]);
  const selectedKeyword = useRef<string>();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAutoCompleteOpen, setIsAutoCompleteOpen] = useState(false);

  const handleKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowDown") {
      const currentIndex = autoCompleteRef.current.findIndex(
        (item) => item === e.target,
      );
      const nextIndex = currentIndex + 1;
      if (nextIndex < autoCompleteRef.current.length) {
        autoCompleteRef.current[nextIndex]?.focus();
      }
    }

    if (e.key === "ArrowUp") {
      const currentIndex = autoCompleteRef.current.findIndex(
        (item) => item === e.target,
      );
      const prevIndex = currentIndex - 1;
      if (prevIndex >= 0) {
        autoCompleteRef.current[prevIndex].focus();
      } else {
        inputRef.current?.focus();
      }
    }

    if (e.key === "Enter" && e.currentTarget.textContent) {
      const currentIndex = autoCompleteRef.current.findIndex(
        (item) => item === e.target,
      );
      onSelect &&
        onSelect(autoCompleteRef.current[currentIndex].textContent ?? "");

      selectedKeyword.current =
        autoCompleteRef.current[currentIndex].textContent ?? "";

      inputRef.current?.focus();
      setIsAutoCompleteOpen(false);
    }
  };

  const handleAutoCompleteClick = (e: React.MouseEvent<HTMLDivElement>) => {
    onSelect && onSelect(e.currentTarget.textContent ?? "");
    selectedKeyword.current = e.currentTarget.textContent ?? "";

    inputRef.current?.focus();
    setIsAutoCompleteOpen(false);
    inputRef.current?.blur();
  };

  const handleOutsideClick = (e: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(e.target as Node)
    ) {
      setIsAutoCompleteOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setIsAutoCompleteOpen(false);
    } else {
      if (selectedKeyword.current === autocomplete?.[0]?.label) return;
      setIsAutoCompleteOpen(autocomplete ? autocomplete.length !== 0 : false);
    }
  }, [autocomplete]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      {children(autoCompleteRef)}
      <AutoCompleteBox
        isOpen={isAutoCompleteOpen}
        onKeyUp={handleKeyUp}
        onKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        {autocomplete?.map((item, index) => (
          <div
            onClick={handleAutoCompleteClick}
            ref={(el) => (autoCompleteRef.current[index] = el!)}
            key={index}
            tabIndex={index}
          >
            {item.label}
          </div>
        ))}
      </AutoCompleteBox>
    </div>
  );
};

export default AutoComplete;

const AutoCompleteBox = styled.div<{ isOpen: boolean }>`
  position: absolute;
  left: 0;
  width: 100%;
  ${({ theme, isOpen }) => css`
    border: 1px solid ${theme.color.stroke.dark};
    border-radius: 5px;

    display: ${isOpen ? "block" : "none"};

    ${theme.typography.body3.regular};
    background-color: ${theme.color.background.white};
    color: ${theme.color.text.secondary};

    > div {
      padding: 1.2rem 1.4rem;

      ${theme.typography.body3.regular};
      width: 100%;

      :hover {
        background-color: ${theme.color.background.dark};
      }
      :focus {
        outline: none;
        background-color: ${theme.color.background.dark};
      }
    }
  `}
`;
